import { computed, onMounted, onUnmounted, ref, watch, type ComputedRef, type Ref } from 'vue'
import type { Comic } from '@tanko/shared'
import { useLibraryStore } from '../stores/library'
import { useCloudLibraryStore } from '../stores/cloud-library'
import { FolderSource } from '../sources/folder-source'
import { CloudApiClient } from './api-client'
import { db } from '../db/schema'
import { resolveComicDirectory } from './library-sources'
import { isBackedUp } from './comic-sync'

export type ComicScope = 'local' | 'cloud'

export type ComicCoverState = {
  /** 掛在作品外框上，用於延遲載入的觀察目標。 */
  element: Ref<HTMLElement | undefined>
  /** 可用時是 object URL，否則為空字串。 */
  cover: Ref<string>
  failed: Ref<boolean>
  sourceAuthorized: ComputedRef<boolean>
  /** 作品目前是否可以開啟閱讀。 */
  available: ComputedRef<boolean>
  /** 取不到封面時要顯示的替代文字。書架卡片與列表列共用同一份，因此兩者不會分岔。 */
  fallbackText: ComputedRef<string>
}

/**
 * 取得一本作品的封面。
 *
 * 先查 IndexedDB 的封面縮圖快取，命中且頁碼相符就用它；否則在來源已授權時才讀原圖
 * ——這一步才依賴資料夾權限，所以書庫的瀏覽本身不會被權限擋住。
 *
 * 由書架卡片與列表列共用，兩種呈現的延遲載入時機、替代文字與清理順序因此完全相同。
 */
export function useComicCover(comic: () => Comic, scope: () => ComicScope): ComicCoverState {
  const store = useLibraryStore()
  const cloud = useCloudLibraryStore()
  const api = new CloudApiClient()
  const element = ref<HTMLElement>()
  const visible = ref(false)
  const cover = ref('')
  const failed = ref(false)
  const source = computed(() => scope() === 'cloud' ? undefined : store.sourceOf(comic()))
  const sourceAuthorized = computed(() => !!source.value && store.isAuthorized(source.value.id))
  // 尚未備份的作品在雲端沒有頁面，因此既不可讀也沒有縮圖可取。
  const backedUp = computed(() => isBackedUp(comic()))
  const available = computed(() => scope() === 'cloud'
    ? backedUp.value
    : comic().indexed && sourceAuthorized.value && !store.missing.has(comic().id))
  const fallbackText = computed(() => {
    if (scope() === 'cloud') {
      if (!backedUp.value) return '尚未備份'
      return failed.value ? '雲端封面無法顯示' : '正在取書…'
    }
    if (store.missing.has(comic().id)) return '找不到檔案'
    if (!sourceAuthorized.value) return '等待授權來源'
    return failed.value ? '封面無法顯示' : '正在取書…'
  })

  let observer: IntersectionObserver | undefined
  /*
    來源必須是「多個 getter 的陣列」，不能是「一個回傳陣列的 getter」。後者每次回傳新陣列，
    Vue 只比對陣列本身，於是任何相依變動都會觸發回呼——而 getter 讀了 comic()，清單每重算一次
    每張卡片就拿到身分不同的作品物件，封面跟著撤銷 URL、清空、重讀縮圖，畫面上就是整頁閃一下；
    批次分類讓清單重算二十次，就閃二十次。多來源陣列逐元素以 Object.is 比對，九個值都沒變就不動。
  */
  watch([
    () => visible.value,
    () => scope(),
    () => sourceAuthorized.value,
    () => store.missing.has(comic().id),
    () => comic().id,
    () => store.refreshing,
    () => comic().coverPage,
    () => comic().syncState,
    () => cloud.coverVersion(comic().id),
  ], async ([isVisible, currentScope, authorized, missing, , refreshing], _, cleanup) => {
      let cancelled = false
      let url: string | undefined
      let folder: FolderSource | undefined
      const controller = new AbortController()
      cleanup(() => { cancelled = true; controller.abort(); if (url) URL.revokeObjectURL(url); void folder?.dispose() })
      cover.value = ''
      failed.value = false
      if (!isVisible || refreshing) return
      try {
        if (currentScope === 'cloud') {
          // 封面縮圖一本一張，記在 comics 上；尚未備份的作品沒有——請求必然 404。
          if (!backedUp.value) return
          const thumbnail = await api.coverImage(comic().id, cloud.coverVersion(comic().id), controller.signal)
          if (cancelled) return
          url = URL.createObjectURL(thumbnail)
          cover.value = url
          return
        }
        const thumbnail = await db.coverThumbnails.get(comic().id)
        if (cancelled) return
        if (thumbnail && thumbnail.page === comic().coverPage) {
          url = URL.createObjectURL(thumbnail.blob)
          cover.value = url
          return
        }
        // 沒有快取縮圖時才需要讀原圖，這一步才依賴資料夾權限。
        if (!authorized || missing || !source.value) return
        const directory = await resolveComicDirectory(source.value.handle, comic().relPath)
        folder = await FolderSource.create(directory)
        const file = await folder.getPage(comic().coverPage)
        if (cancelled) return
        url = URL.createObjectURL(file)
        cover.value = url
      } catch { if (!cancelled) failed.value = true }
      finally { await folder?.dispose() }
    }, { immediate: true })

  onMounted(() => {
    observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { visible.value = true; observer?.disconnect() }
    }, { rootMargin: '250px' })
    if (element.value) observer.observe(element.value)
  })
  onUnmounted(() => observer?.disconnect())

  return { element, cover, failed, sourceAuthorized, available, fallbackText }
}
